// src/hooks/useLicense.js — LIC-106
//
// Reaktiv hook över backend-licens-state (verifierad Ed25519-signatur).
// Läser /api/config.license, mappar backend-modul-IDs till frontend-IDs,
// och triggar omrendrering när licensen uppdateras (efter upload via
// LicenseUploadPanel emittas 'rms_backend_license_changed'-event).
//
// Returnerar:
//   {
//     state: 'valid' | 'expired_grace' | 'expired_locked' | 'invalid' |
//            'missing' | 'not_initialized' | 'unknown',
//     modules: string[]  — frontend-module-IDs ('bwra', 'geographical-risk' osv)
//     maxUsers: number | null
//     expiresAt: string | null  (ISO-date)
//     daysUntilExpiry: number | null
//     daysSinceExpiry: number | null
//     customer: string | null
//     loading: boolean
//     enforcing: boolean   — true om license-state ska respekteras (valid/grace/locked)
//     hasModule: (frontendId) => boolean   — convenience helper
//   }
//
// Failsafe: om backend är onåbart eller state är 'missing'/'invalid' →
// enforcing=false → existerande frontend-licens-system används som vanligt
// (backåt-kompatibelt med våg 4-installer som inte har .lic-fil).

import { useEffect, useState } from 'react';
import { fetchConfig, getConfigSync } from '../api/config';

// Backend-utfärdade module-IDs (från scripts/issue-license.js) → frontend-IDs.
// Backend använder kortare IDs för bekvämlighet vid licens-utfärdning.
// 'admin' och 'dashboard' är alltid tillgängliga (alwaysEnabled i SettingsService)
// och behöver inte vara med i licensen.
export const BACKEND_TO_FRONTEND_MODULE_ID = {
  bwra: 'bwra',
  georisk: 'geographical-risk',
  customerrisk: 'customer-risk',
  txmon: 'transaction-monitoring',
  periodicreporting: 'periodic-reporting',
  compliance: 'bwra',  // compliance-tabben ligger i BWRA-modulen
};

const ENFORCING_STATES = new Set(['valid', 'expired_grace', 'expired_locked']);

function mapModules(backendModules) {
  if (!Array.isArray(backendModules)) return [];
  const mapped = backendModules
    .map((m) => BACKEND_TO_FRONTEND_MODULE_ID[m])
    .filter(Boolean);
  return Array.from(new Set(mapped));
}

function deriveLicense(config) {
  if (!config || !config.license) {
    return {
      state: 'unknown',
      modules: [],
      maxUsers: null,
      expiresAt: null,
      daysUntilExpiry: null,
      daysSinceExpiry: null,
      customer: null,
      enforcing: false,
    };
  }
  const lic = config.license;
  return {
    state: lic.state || 'unknown',
    modules: mapModules(lic.modules),
    maxUsers: typeof lic.maxUsers === 'number' ? lic.maxUsers : null,
    expiresAt: lic.expiresAt || null,
    daysUntilExpiry: lic.daysUntilExpiry ?? null,
    daysSinceExpiry: lic.daysSinceExpiry ?? null,
    customer: lic.customer || null,
    enforcing: ENFORCING_STATES.has(lic.state),
  };
}

const LICENSE_CHANGED_EVENT = 'rms_backend_license_changed';

export function notifyBackendLicenseChanged() {
  window.dispatchEvent(new Event(LICENSE_CHANGED_EVENT));
}

export function useLicense() {
  const [config, setConfig] = useState(getConfigSync());
  const [loading, setLoading] = useState(!config);

  useEffect(() => {
    let cancelled = false;

    const loadConfig = (force = false) => {
      fetchConfig({ force })
        .then((cfg) => {
          if (!cancelled) {
            setConfig(cfg);
            setLoading(false);
          }
        })
        .catch(() => {
          if (!cancelled) setLoading(false);
        });
    };

    if (!config) loadConfig();

    const handleChange = () => loadConfig(true);
    window.addEventListener(LICENSE_CHANGED_EVENT, handleChange);
    return () => {
      cancelled = true;
      window.removeEventListener(LICENSE_CHANGED_EVENT, handleChange);
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const license = deriveLicense(config);

  return {
    ...license,
    loading,
    hasModule: (frontendModuleId) => license.modules.includes(frontendModuleId),
  };
}

export default useLicense;
